import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import styles from './UpsShipmentForm.module.sass';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import Dproject from '../../../../models/Dproject';

const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.content;

const isGpBarcode = (input) => /^gp\d+$/i.test(input?.trim());
const parseGpId = (input) => input.trim().replace(/^gp/i, '');

const Form = ({ closeModal }) => {
  const { register, handleSubmit, setValue, setFocus, watch } = useForm();
  const [isShipping, setIsShipping] = useState(false);
  const [shipments, setShipments] = useState([]);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const [voidingId, setVoidingId] = useState(null);
  const [schoolName, setSchoolName] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [historyStartDate, setHistoryStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const [historyEndDate, setHistoryEndDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  const watchProjectId = watch('project_id');
  const watchWeight = watch('weight_lbs');

  useEffect(() => {
    loadShipments();
    setTimeout(() => setFocus('project_id'), 100);
  }, []);

  const lookupProject = useCallback(() => {
    const raw = watchProjectId?.trim();
    if (!raw) {
      setSchoolName(null);
      return;
    }
    setLookingUp(true);

    if (isGpBarcode(raw)) {
      const gpId = parseGpId(raw);
      axios
        .get(`/api/v1/generated_products/${gpId}/lookup`)
        .then((res) => {
          const label = res.data.product_type
            ? `${res.data.school_name} — ${res.data.product_type}`
            : res.data.school_name || 'Unknown school';
          setSchoolName(label);
        })
        .catch(() => setSchoolName('Product not found'))
        .finally(() => setLookingUp(false));
    } else {
      Dproject.find(raw)
        .then((res) => {
          setSchoolName(res.data.schoolName || 'Unknown school');
        })
        .catch(() => setSchoolName('Project not found'))
        .finally(() => setLookingUp(false));
    }
  }, [watchProjectId]);

  const loadShipments = () => {
    setLoadingShipments(true);
    axios
      .get('/api/v1/dprojects/todays_shipments')
      .then((res) => setShipments(res.data))
      .catch(() => setShipments([]))
      .finally(() => setLoadingShipments(false));
  };

  const onSubmit = (data) => {
    if (!data.project_id || !data.weight_lbs) return;

    const raw = data.project_id.trim();
    const isGp = isGpBarcode(raw);
    const id = isGp ? parseGpId(raw) : raw;
    const url = isGp
      ? `/generated_products/${id}/quick_ship.json`
      : `/dprojects/${id}/quick_ship.json`;
    const label = isGp ? `GP${id}` : `Project #${id}`;

    setIsShipping(true);
    axios
      .post(url, {
        weight_lbs: data.weight_lbs,
        authenticity_token: csrfToken()
      })
      .then((res) => {
        toast.success(
          `${label} shipped — Tracking: ${res.data.tracking}`,
          { position: 'bottom-left', autoClose: 5000 }
        );
        setValue('project_id', '');
        setValue('weight_lbs', '');
        setSchoolName(null);
        loadShipments();
        setTimeout(() => setFocus('project_id'), 100);
      })
      .catch((err) => {
        const errors = err.response?.data?.errors;
        const msg = errors ? errors.join(', ') : 'Shipment failed. Check ID and try again.';
        toast.error(`${label}: ${msg}`, {
          position: 'bottom-left',
          autoClose: 5000
        });
        setTimeout(() => setFocus('project_id'), 100);
      })
      .finally(() => setIsShipping(false));
  };

  const handleVoid = (shipment) => {
    const displayId = shipment.type === 'product'
      ? `${shipment.id} — ${shipment.tracking}`
      : `Project #${shipment.id}`;
    if (!confirm(`Void shipment for ${displayId}?`)) return;

    const numericId = shipment.type === 'product'
      ? shipment.id.replace(/^GP/i, '')
      : shipment.id;
    const url = shipment.type === 'product'
      ? `/generated_products/${numericId}/void_shipment.json`
      : `/dprojects/${numericId}/void_shipping_label.json`;

    const voidKey = shipment.label_id || shipment.id;
    setVoidingId(voidKey);
    const postData = { authenticity_token: csrfToken() };
    if (shipment.type === 'product' && shipment.label_id) {
      postData.label_id = shipment.label_id;
    }
    axios
      .post(url, postData)
      .then(() => {
        toast.success(`${displayId} shipment voided`, {
          position: 'bottom-left',
          autoClose: 3000
        });
        loadShipments();
      })
      .catch((err) => {
        const errors = err.response?.data?.errors;
        const msg = errors ? errors.join(', ') : 'Could not void shipment.';
        toast.error(`${displayId}: ${msg}`, {
          position: 'bottom-left',
          autoClose: 5000
        });
      })
      .finally(() => setVoidingId(null));
  };

  const openManifest = () => {
    window.open('/dprojects/create_shipping_manifest', '_blank');
  };

  const openShipmentHistory = () => {
    window.open(
      `/dprojects/shipment_history?start_date=${historyStartDate}&end_date=${historyEndDate}`,
      '_blank'
    );
  };

  return (
    <div className={styles.form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.inputGroup}>
          <div className={styles.field}>
            <label className={styles.label}>Project / Product ID *</label>
            <input
              className={styles.input}
              type="text"
              autoFocus
              placeholder="e.g. 1234 or GP5678"
              {...register('project_id', { required: true })}
              onBlur={lookupProject}
            />
            {(schoolName || lookingUp) && (
              <span className={styles.schoolName}>
                {lookingUp ? 'Looking up...' : schoolName}
              </span>
            )}
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Weight (lbs) *</label>
            <input
              className={styles.input}
              type="number"
              step="0.1"
              min="0.1"
              {...register('weight_lbs', { required: true })}
            />
          </div>
          <button
            type="submit"
            className={styles.shipBtn}
            disabled={!watchProjectId || !watchWeight || isShipping}
          >
            {isShipping ? (
              <><span className={styles.spinner} /> Shipping...</>
            ) : (
              'Ship'
            )}
          </button>
        </div>
      </form>

      <div className={styles.sectionHeader}>Today's Shipments</div>

      <div className={styles.shipmentsContainer}>
        {loadingShipments ? (
          <div className={styles.emptyMessage}>Loading...</div>
        ) : shipments.length === 0 ? (
          <div className={styles.emptyMessage}>No shipments today</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Type</th>
                <th>School</th>
                <th>Tracking #</th>
                <th>Cost</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {shipments.map((s, idx) => {
                const voidKey = s.label_id || s.id;
                return (
                <tr key={s.label_id || `${s.id}-${idx}`}>
                  <td>{s.id}</td>
                  <td>{s.product}</td>
                  <td>{s.school}</td>
                  <td>{s.tracking}</td>
                  <td>{s.cost ? `$${Number(s.cost).toFixed(2)}` : '—'}</td>
                  <td>
                    {s.label_url && (
                      <a
                        href={s.label_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.printBtn}
                        title="Print Label"
                      >
                        Print
                      </a>
                    )}
                    <button
                      type="button"
                      className={styles.voidBtn}
                      disabled={voidingId === voidKey}
                      onClick={() => handleVoid(s)}
                    >
                      {voidingId === voidKey ? 'Voiding...' : 'Void'}
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <button
        type="button"
        className={styles.manifestBtn}
        onClick={openManifest}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd"/>
        </svg>
        Print End of Day Manifest
      </button>

      <div className={styles.historySection}>
        <div className={styles.sectionHeader}>Shipment History</div>
        <div className={styles.historyControls}>
          <div className={styles.field}>
            <label className={styles.label}>Start Date</label>
            <input
              className={styles.input}
              type="date"
              value={historyStartDate}
              onChange={(e) => setHistoryStartDate(e.target.value)}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>End Date</label>
            <input
              className={styles.input}
              type="date"
              value={historyEndDate}
              onChange={(e) => setHistoryEndDate(e.target.value)}
            />
          </div>
          <button
            type="button"
            className={styles.historyBtn}
            onClick={openShipmentHistory}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm2 10a1 1 0 10-2 0v3a1 1 0 102 0v-3zm2-3a1 1 0 011 1v5a1 1 0 11-2 0v-5a1 1 0 011-1zm4 2a1 1 0 10-2 0v3a1 1 0 102 0v-3z" clipRule="evenodd"/>
            </svg>
            Shipment History
          </button>
        </div>
      </div>
    </div>
  );
};

export default Form;
